import { useCallback, useEffect, useState } from 'react';
import { useBabies } from './queries';
import { rowToBaby } from './derive';
import type { Baby } from './types';
import type { BabyRow } from './dto';

const STORAGE_KEY = 'babysteps.active_baby_id';

function read(): string | null {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
}
function write(id: string | null): void {
  try {
    if (id === null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, id);
  } catch { /* no-op */ }
}

interface ActiveBabyState {
  /** True while /babies is still resolving on first load. */
  loading: boolean;
  /** Server rows for every baby the user can access. */
  babies: BabyRow[];
  /** UI-shaped active baby (initials, ageWeeks, preset…). null if no babies yet. */
  baby: Baby | null;
  /** Active baby id (UUID string). null if no babies yet. */
  activeId: string | null;
  /** Switch which baby is active. Persists to localStorage. */
  setActiveId: (id: string) => void;
  /** Raw error from /babies, if any. */
  error: unknown;
}

/**
 * Single source of truth for "which baby is the UI currently showing".
 *
 * Pulls the baby list via TanStack Query, derives a UI-shaped active baby,
 * and persists the selection in localStorage so reloads keep the same baby
 * active.
 *
 * Strategy:
 *   - If the stored id is in the babies list → use it
 *   - Otherwise use the first baby
 *   - If there are zero babies → activeId stays null (callers should render
 *     an empty state)
 */
export function useActiveBaby(enabled = true): ActiveBabyState {
  const { data: babies, isLoading, error } = useBabies(enabled);
  const [activeId, setActiveIdState] = useState<string | null>(() => read());

  // Reconcile when the babies list arrives or changes.
  useEffect(() => {
    if (!babies) return;
    const has = (id: string | null): boolean => !!id && babies.some((b) => b.id === id);
    if (has(activeId)) return;
    const next = babies[0]?.id ?? null;
    setActiveIdState(next);
    write(next);
  }, [babies, activeId]);

  const setActiveId = useCallback((id: string) => {
    setActiveIdState(id);
    write(id);
  }, []);

  const activeRow = babies?.find((b) => b.id === activeId) ?? null;

  return {
    loading: isLoading,
    babies: babies ?? [],
    baby: activeRow ? { ...rowToBaby(activeRow), current: true } : null,
    activeId,
    setActiveId,
    error,
  };
}
